/*!
 *  Taxonomy field.
 *
 *  @prop string className - Append a class name.
 *  @prop boolean disabled - Whether the field should be disabled.
 *  @prop boolean error - Whether this field has an erroneous value. 
 *  @prop string id - Field ID.
 *  @prop string label - Field label.
 *  @prop function onChange - Callback for when the field value has changed.
 *  @prop function|object pool - Taxonomy pool for providing term suggestions.
 *  @prop string value - Field value.
 * 
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React from "react";
import PropTypes from "prop-types";
import "./taxonomyfield.scss";
import Globals from "Class/Globals";
import {ObjectCompare, Slug, StripTags} from "Functions";
import IconItem from "Components/UI/IconItem";
import IconButton from "Components/UI/IconButton";
import Item from "Components/UI/Item";
import TextareaField from "Components/UI/Field/TextareaField";
import TextField from "Components/UI/Field/TextField";
import Wysiwyg from "Components/UI/Wysiwyg";

class TaxonomyField extends React.Component
{
    constructor(props)
    {
        super(props);
        this.AllowReorder = true;
        this.Block = false;
        this.Mounted = false;
        this.TaxonomyElements = {};
        this.TransportElements = {};
        this.TermCache = {};
        this.TermInputs = {};
        this.TermItems = {};
        this.TermEditInputs = {};
        this.Thresholds = [];
        this.state =
        {
            focus: false,
            hoverItem: [],
            items: [],
            origin: false,
            suggestions: false,
            taxonomyIndex: -1,
            taxonomySort: false,
            termReorder: false,
            value: {}
        };
    }

    /**
     * Set initial value.
     * @return void
     */

    componentDidMount()
    {
        this.Mounted = true;
        const {value} = this.props;
        this.setState({value});
    }

    /**
     * Update value.
     * @return void
     */

    componentDidUpdate(prevProps)
    {
        if (this.Block)
        {
            return;
        }
        const {value: v1} = this.props;
        const {value: v2} = this.state;
        const {value: v3} = prevProps;
        if (!ObjectCompare(v1, v2) && !ObjectCompare(v1, v3))
        {   
            this.Block = true;
            this.setState({value: v1}, () => this.Block = false);
        }
    }

    componentWillUnmount()
    {
        this.Mounted = false;
    }

    AddTerm = (taxonomyKey, termName) =>
    {
        const {id, onChange} = this.props;
        const Name = StripTags(termName).replace("\n", "");
        const Key = Slug(Name);
        const Pool = this.GetPool();
        const Value = this.Value();
        if (!Key.length || Pool[taxonomyKey] === undefined || Value[taxonomyKey]?.terms[Key] !== undefined)
        {
            this.TermInputs[taxonomyKey]?.SetContent("");
            return;
        }
        if (Value[taxonomyKey] === undefined)
        {
            Value[taxonomyKey] = {description: "", name: Pool[taxonomyKey].name, order: -1, terms: {}};
        }
        Value[taxonomyKey].terms[Key] = [Name, -1];
        onChange(null, Value, id);
        if (Pool[taxonomyKey] !== undefined && Pool[taxonomyKey].terms[Key] === undefined)
        {
            Pool[taxonomyKey].terms[Key] = [Name, -1];
        }
        setTimeout(() =>
        {
            this.setState({value: Value}, () => this.TermInputs[taxonomyKey]?.SetContent(""));
        });
    }

    GetPool = () =>
    {
        const {id, pool} = this.props;
        const {value} = this.state;
        return typeof pool === "function" ? pool(id) : pool || (typeof value === "object" ? value : {});
    }

    OnAddTaxonomy = (e) =>
    {
        const {id, onChange} = this.props;
        const Pool = this.GetPool();
        const Value = this.Value();
        let Key = "category";
        let Name = "Category";
        let Index = 1;
        while (Pool[Key] !== undefined)
        {
            Index++;
            Key = "category" + Index;
            Name = "Category " + Index;
        }
        const Taxonomy = {description: "", name: Name, order: -1, terms: {}};
        Value[Key] = Pool[Key] = Taxonomy;
        onChange(e, Value, id);
        setTimeout(() =>
        {
            if (!this.Mounted)
            {
                return;
            }
            this.setState({value: Value});
        });
    }

    OnClickSuggestion = (e, [taxonomyKey, termName]) =>
    {
        clearTimeout(this.BlurTimeout);
        this.AddTerm(taxonomyKey, termName);
        this.setState({suggestions: false});
    }

    OnRemoveTaxonomy = (e, key) =>
    {
        Globals.DialogCreate({
            confirmLabel: "Delete Taxonomy",
            message: "Are you sure that you want to delete this taxomomy?",
            onConfirm: () =>
            {
                const {id, onChange, onRemoveTaxonomy} = this.props;
                const Pool = this.GetPool();
                const Value = this.Value();
                delete Pool[key];
                delete Value[key];
                onChange(e, Value, id);
                onRemoveTaxonomy(e, key, id);
                setTimeout(() =>
                {
                    if (!this.Mounted)
                    {
                        return;
                    }
                    this.setState({value: Value});
                });
            },
            title: "Delete Taxonomy",
            type: "confirm"
        });
    }

    OnSortTaxonomyDown = (e, key) => this.OnSortTaxonomyStep(e, key, +1);
    OnSortTaxonomyUp = (e, key) => this.OnSortTaxonomyStep(e, key, -1);

    OnSortTaxonomyEnd = (e) =>
    {
        e.stopPropagation();
        e.preventDefault();
        const {taxonomyIndex, taxonomySort} = this.state;
        const Element = this.TransportElements[taxonomySort];
        window.removeEventListener("mousemove", this.OnSortTaxonomyMove);
        window.removeEventListener("mouseup", this.OnSortTaxonomyEnd);
        this.setState({
            origin: false,
            taxonomyIndex: -1,
            taxonomySort: false
        }, () =>
        {
            const MaxIndex = Object.keys(this.GetPool()).length - 1;
            Element.style.transform = "";
            this.OnSortTaxonomyStep(e, taxonomySort, Math.min(taxonomyIndex, MaxIndex), true);
        });
    }

    OnSortTaxonomyMove = (e) =>
    {
        e.stopPropagation();
        e.preventDefault();
        const {origin, taxonomyIndex, taxonomySort} = this.state;
        const {pageX, pageY} = e;
        const [originX, originY] = origin;
        const Element = this.TransportElements[taxonomySort];
        if (!Element)
        {
            return;
        }
        const X = pageX - originX;
        const Y = pageY - originY;
        Element.style.transform = `translate(${X}px, ${Y}px)`;
        let Index = 0;
        for (let i in this.Thresholds)
        {
            if (pageY < this.Thresholds[i])
            {
                break;
            }
            Index++;
        }
        if (!Index !== taxonomyIndex)
        {
            this.setState({taxonomyIndex: Index});
        }
    }

    OnSortTaxonomyStart = (e, key) =>
    {
        if (e.button !== 0)
        {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        const {pageX, pageY} = e;
        const CurrentOrder = this.SortTaxonomies().map(({key}) => key);
        window.addEventListener("mousemove", this.OnSortTaxonomyMove);
        window.addEventListener("mouseup", this.OnSortTaxonomyEnd);
        this.Thresholds = [];
        CurrentOrder.forEach(taxonomyKey =>
        {
            let Taxonomy = this.TaxonomyElements[taxonomyKey];
            if (!Taxonomy)
            {
                return;
            }
            let {top, height} = Taxonomy.getBoundingClientRect();
            this.Thresholds.push(top + height / 2);
        });
        this.setState({origin: [pageX, pageY], taxonomySort: key});
    }

    OnSortTaxonomyStep = (e, key, direction, absolute = false) =>
    {
        const {id, onTaxonomyOrder} = this.props;
        const CurrentOrder = this.SortTaxonomies().map(({key}) => key);
        const IndexFrom = CurrentOrder.indexOf(key);
        const IndexTo = absolute ? direction : IndexFrom + direction;
        if (IndexFrom < 0 || IndexTo < 0 || IndexTo >= CurrentOrder.length)
        {
            return;
        }
        const NewOrder = Array.from(CurrentOrder);
        NewOrder.splice(IndexTo, 0, NewOrder.splice(IndexFrom, 1)[0]);
        onTaxonomyOrder(e, NewOrder, id);
        this.forceUpdate();
    }

    OnTaxonomyDescription = (e, description, key) =>
    {
        const {id, onTaxonomyDescription} = this.props;
        const Pool = this.GetPool();
        const Value = this.Value();
        const PoolTaxonomy = Pool[key] || {};
        const ValueTaxonomy = Value[key] || {};
        PoolTaxonomy.description = ValueTaxonomy.description = description;
        onTaxonomyDescription(e, key, description, id);
        setTimeout(() =>
        {
            if (!this.Mounted)
            {
                return;
            }
            this.setState({value: Value});
        });
    }

    OnTaxonomyName = (e, newName, oldKey) =>
    {
        const {id, onTaxonomyName} = this.props;
        const Pool = this.GetPool();
        const Value = this.Value();
        const DesiredKey = newName ? Slug(newName) : "category";
        const DesiredName = newName || "Category";
        const PoolTaxonomy = Pool[oldKey] || {};
        const ValueTaxonomy = Value[oldKey] || {};
        let Key = DesiredKey;
        let Name = DesiredName;
        let Index = 1;
        while (Pool[Key] !== undefined)
        {
            Index++;
            Key = DesiredKey + Index;
            Name = DesiredName + " " + Index;
        }
        PoolTaxonomy.name = ValueTaxonomy.name = Name;
        delete Pool[oldKey];
        delete Value[oldKey];
        Pool[Key] = PoolTaxonomy;
        Value[Key] = ValueTaxonomy;
        onTaxonomyName(e, oldKey, Key, Name, id);
        setTimeout(() =>
        {
            if (!this.Mounted)
            {
                return;
            }
            this.setState({value: Value});
        });
    }

    OnTermBlur = (e, termName, taxonomyKey) =>
    {
        if (!this.Mounted)
        {
            return;
        }
        clearTimeout(this.BlurTimeout);
        this.BlurTimeout = setTimeout(() => 
        {
            this.AddTerm(taxonomyKey, termName);
            this.setState({suggestions: false});
        }, 200);
    }

    OnTermEditBlur = (e, termName, [termKey, taxonomyKey, termOrder]) =>
    {
        const {id, onEditTerm} = this.props;
        const OldName = this.TermCache[taxonomyKey + termKey] || "";
        const NewName = (termName || OldName).replace("\n", "");
        if (OldName !== NewName)
        {
            onEditTerm(e, OldName, NewName, termKey, taxonomyKey, id, termOrder);
        }
        setTimeout(() =>
        {
            this.TermEditInputs[taxonomyKey + termKey]?.SetContent(NewName);
        });
    }

    OnTermEditFocus = (e, termName, [termKey, taxonomyKey]) =>
    {
        this.TermCache[taxonomyKey + termKey] = termName;
    }

    OnTermEditInput = (e, termName, key) =>
    {
        switch (e.getLastChangeType())
        {
            case "split-block":
                this.OnTermEditBlur(e, termName, key);
                break;
            default:
        }
    }

    OnTermInput = (e, value, key) =>
    {
        switch (e.getLastChangeType())
        {
            case "split-block":
                this.AddTerm(key, value);
                break;
            default:
                this.ShowSuggestions(key, value);
                break;
        }
    }

    OnTermInputClick = (e, key) =>
    {
        this.TermInputs[key]?.Focus();
    }

    OnTermItem = (item, taxonomyKey, termKey) =>
    {
        if (this.TermItems[taxonomyKey] === undefined)
        {
            this.TermItems[taxonomyKey] = {};
        }
        if (!item)
        {
            delete this.TermItems[taxonomyKey][termKey];
        }
        else
        {
            this.TermItems[taxonomyKey][termKey] = item;
        }
    }

    OnTermRemove = (e, [taxonomyKey, termKey]) =>
    {
        this.RemoveTerm(taxonomyKey, termKey);
    }

    OnTermReorderEnd = (e) =>
    {
        const {id, onEditTerms} = this.props;
        e.stopPropagation();
        e.preventDefault();
        window.removeEventListener("mousemove", this.OnTermReorderMove);
        window.removeEventListener("mouseup", this.OnTermReorderEnd);
        const {hoverItem, termReorder} = this.state;
        if (hoverItem.length)
        {
            const [taxonomyKey, termKey] = termReorder;
            const [targetTermKey, indexOffset] = hoverItem;
            const Pool = this.GetPool();
            const Taxonomy = Pool[taxonomyKey] || {terms: {}};
            const Terms = [];
            (this.SortTaxonomy(Taxonomy).terms || {}).map(term => Terms.push(term.key));
            Terms.splice(Terms.indexOf(termKey), 1);
            Terms.splice(Terms.indexOf(targetTermKey) + indexOffset, 0, termKey);
            Terms.forEach((termKey, order) =>
            {
                const TermName = Array.isArray(Taxonomy.terms[termKey]) ? Taxonomy.terms[termKey][0] : Taxonomy.terms[termKey];
                Taxonomy.terms[termKey] = [TermName, parseInt(order)];
            });
            onEditTerms(taxonomyKey, Taxonomy.terms, id);
        }
        this.setState({
            hoverItem: [],
            items: [],
            origin: false,
            termReorder: false
        });
    }

    OnTermReorderMove = (e) =>
    {
        e.stopPropagation();
        e.preventDefault();
        const {hoverItem, items} = this.state;
        const {pageX, pageY} = e;
        const [originX, originY, offsetX, offsetY] = this.state.origin;
        const X = pageX - originX;
        const Y = pageY - originY;
        this.Cursor.style.transform = `translate(${X + offsetX}px, ${Y + offsetY}px)`;
        let Item = [];
        for (let index in items)
        {
            let [key, left, top, width, height] = items[index];
            if (X < left || X > left + width || Y < top || Y > top + height)
            {
                continue;
            }
            Item = [key, (X - left) / width < 0.5 ? 0 : 1];
            break;
        }
        if (Item[0] === hoverItem[0] && Item[1] === hoverItem[1])
        {
            return;
        }
        this.setState({hoverItem: Item});
    }

    OnTermReorderStart = (e, key) =>
    {
        if (e.button !== 0)
        {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        const {pageX, pageY} = e;
        const {offsetLeft, offsetTop} = e.currentTarget.parentNode;
        const {top, left} = e.currentTarget.parentNode.parentNode.getBoundingClientRect();
        const Offset = [offsetLeft, offsetTop];
        window.addEventListener("mousemove", this.OnTermReorderMove);
        window.addEventListener("mouseup", this.OnTermReorderEnd);
        this.setState({origin: [pageX, pageY, ...Offset], termReorder: key}, () =>
        {
            this.Cursor.style.transform = `translate(${Offset[0]}px, ${Offset[1]}px)`;
            const Items = [];
            for (let termKey in this.TermItems[key[0]])
            {
                const Item = this.TermItems[key[0]][termKey];
                Items.push([termKey, left + Item.offsetLeft - pageX, top + Item.offsetTop - pageY, Item.offsetWidth, Item.offsetHeight]);
            }
            this.setState({items: Items});
        });
    }

    RemoveTerm = (taxonomyKey, termKey) =>
    {
        const {id, onChange} = this.props;
        const Pool = this.GetPool();
        const Value = this.Value();
        delete Value[taxonomyKey]?.terms[termKey];
        onChange(null, Value, id);
        setTimeout(() =>
        {
            this.setState({value: Value});
        });
    }

    /**
     * Reset to inital state.
     * @return void
     */

    Reset = () =>
    {
        const {id, onChange, value} = this.props;
        this.setState({value});
        onChange(null, value, id);
    }

    ShowSuggestions = (taxonomyKey, termInputValue) =>
    {
        const Pool = this.GetPool();
        const Value = this.Value();
        if (!termInputValue.match(/[a-z]/i) || Pool[taxonomyKey] === undefined)
        {
            this.setState({suggestions: false})
            return;
        }
        const AvailableTerms = Object.values(Pool[taxonomyKey].terms);
        const ExistingTerms = Object.values(Value[taxonomyKey]?.terms || {}).map(term => typeof term === "object" ? term[0] : term);
        const Match = new RegExp(termInputValue, "i");
        const List = [];
        AvailableTerms.forEach(term =>
        {
            const termName = typeof term === "object" ? term[0] : term;
            if (List.length >= 5 || !termName.match(Match) || ExistingTerms.indexOf(termName) >= 0)
            {
                return;
            }
            List.push(termName);
        });
        this.setState({suggestions: [taxonomyKey, List]})
    }

    SortTaxonomies = () =>
    {
        const Pool = this.GetPool();
        const Value = this.Value();
        const Taxonomies = [];
        for (let taxonomyKey in Pool)
        {
            Taxonomies.push(this.SortTaxonomy(Pool[taxonomyKey], Object.keys(Value[taxonomyKey]?.terms || {}), taxonomyKey));
        }
        Taxonomies.sort(({name: nameA, order: orderA}, {name: nameB, order: orderB}) =>
        {
            if (orderA > orderB) return +1;
            if (orderA < orderB) return -1;
            if (nameA.substr(0, 8) !== "Category" && nameB.substr(0, 8) === "Category") return +1;
            if (nameA.substr(0, 8) === "Category" && nameB.substr(0, 8) !== "Category") return +1;
            return nameA.localeCompare(nameB);
        });
        return Taxonomies;
    }

    SortTaxonomy = (taxonomy, addedTerms = [], taxonomyKey) =>
    {
        const {description = "", name = "", order = -1, terms = []} = taxonomy;
        const Terms = [];
        for (let termKey in terms)
        {
            if (!this.AllowReorder && addedTerms.indexOf(termKey) < 0)
            {
                continue;
            }
            const [termName, termOrder = -1] = Array.isArray(terms[termKey]) ? terms[termKey] : [terms[termKey], -1];
            Terms.push({key: termKey, name: termName, order: termOrder, selected: addedTerms.indexOf(termKey) >= 0});
        }
        Terms.sort(({name: nameA, order: orderA}, {name: nameB, order: orderB}) =>
        {
            if (orderA > orderB) return +1;
            if (orderA < orderB) return -1;
            return nameA.localeCompare(nameB);
        });
        return {description, key: taxonomyKey, name, order, terms: Terms};
    }

    /**
     * Get the field value.
     * @return string - The field value.
     */

    Value = () =>
    {
        const {value} = this.state;
        return typeof value === "object" && !Array.isArray(value) ? value : {};
    }

    render()
    {
        const {
            className,
            disabled,
            error,
            label
        } = this.props;
        const CA = ["Field", "TaxonomyField"];
        const Pool = this.GetPool();
        const Taxonomies = this.SortTaxonomies();
        const Labels = Object.keys(Pool);
        Labels.sort();
        if (disabled)
        {
            CA.push("Disabled");
        }
        if (error)
        {
            CA.push("Error");
        }
        if (className)
        {
            CA.push(className);
        }
        return (
            <div className={CA.join(" ")}>
                {label ? <label>{label}</label> : ""}
                <div className="TaxonomyFieldList">
                    {Taxonomies.map((taxonomy, index) => this.renderTaxonomy(taxonomy, index))}
                </div>
                <IconItem
                    disabled={disabled}
                    feather="Plus"
                    label="Add Taxonomy"
                    onClick={this.OnAddTaxonomy}
                />
            </div>
        );
    }

    renderSuggestions(taxonomyKey, list)
    {
        if (!list.length)
        {
            return "";
        }
        return (
            <div className="TaxonomyFieldTermInputSuggestions">
                {list?.map(termName => (
                    <Item
                        className="TaxonomyFieldTermInputSuggestionsItem"
                        id={[taxonomyKey, termName]}
                        key={termName}
                        label={termName}
                        onClick={this.OnClickSuggestion}
                    />
                ))}
            </div>
        );
    }

    renderTaxonomy({description = "", key = "", name = "", terms = []}, index)
    {
        const {disabled} = this.props;
        const {hoverItem = [], suggestions, taxonomyIndex, taxonomySort, termReorder} = this.state;
        const CA = ["TaxonomyFieldListItem"];
        const TermItems = [];
        const TermReorder = termReorder && termReorder[0] === key ? termReorder[1] : "";
        const TermReorderName = TermReorder ? termReorder[2] : "";
        if (TermReorder)
        {
            CA.push("Reordering");
        }
        if (taxonomySort === key)
        {
            CA.push("Sorting");
        }
        terms.forEach(({key: termKey, name: termName, selected, order}) => 
        {
            const TermCA = [selected ? "TaxonomyFieldTermItem" : "TaxonomyFieldTermItemUnselected"];
            if (TermReorder === termKey)
            {
                TermCA.push("Moving");
            }
            else if (hoverItem[0] === termKey)
            {
                TermCA.push(hoverItem[1] === 0 ? "HoverLeft" : "HoverRight");
            }
            TermItems.push(selected ? (
                    <div
                        className={TermCA.join(" ")}
                        key={termKey}
                        ref={item => this.OnTermItem(item, key, termKey)}
                    >
                        <Wysiwyg
                            className="TaxonomyFieldEditTermInput"
                            disabled={disabled}
                            id={[termKey, key, order]}
                            minimal={2}
                            onBlur={this.OnTermEditBlur}
                            onFocus={this.OnTermEditFocus}
                            onChange={this.OnTermEditInput}
                            ref={input => this.TermEditInputs[key + termKey] = input}
                            value={termName}
                        />
                        {this.AllowReorder ? <IconButton
                            feather="Move"
                            id={[key, termKey, termName]}
                            onMouseDown={this.OnTermReorderStart}
                        /> : ""}
                        <IconButton
                            feather="X"
                            id={[key, termKey]}
                            onClick={this.OnTermRemove}
                        />
                    </div>
                ) : (
                    <div
                        className={TermCA.join(" ")}
                        key={termKey}
                        ref={item => this.OnTermItem(item, key, termKey)}
                    >
                        {termName}
                    </div>
                )
            );
        });
        return (
            <div className={CA.join(" ")} key={key} ref={taxonomy => this.TaxonomyElements[key] = taxonomy}>
                {index === 0 && taxonomyIndex === 0 ? <div className="TaxonomyFieldListItemInsert"></div> : ""}
                <div className="TaxonomyFieldListItemTransport" ref={transport => this.TransportElements[key] = transport}>
                    <div className="TaxonomyFieldListItemButtons">
                        {this.AllowReorder ? (
                            <>
                                <IconButton
                                    className="TaxonomyFieldListItemMove"
                                    disabled={disabled}
                                    feather="Move"
                                    id={key}
                                    onMouseDown={this.OnSortTaxonomyStart}
                                />
                                <IconButton
                                    className="TaxonomyFieldListItemSortUp"
                                    disabled={disabled}
                                    feather="ArrowUp"
                                    id={key}
                                    onClick={this.OnSortTaxonomyUp}
                                />
                                <IconButton
                                    className="TaxonomyFieldListItemSortDown"
                                    disabled={disabled}
                                    feather="ArrowDown"
                                    id={key}
                                    onClick={this.OnSortTaxonomyDown}
                                />
                            </>
                        ) : ""}
                        <IconButton
                            className="TaxonomyFieldListItemRemove"
                            disabled={disabled}
                            feather="X"
                            id={key}
                            onClick={this.OnRemoveTaxonomy}
                        />
                    </div> 
                    <div className="TaxonomyFieldName">{name}</div>
                    <TextField
                        className="TaxonomyFieldNameInput"
                        disabled={disabled}
                        feather="Edit2"
                        id={key}
                        onChange={this.OnTaxonomyName}
                        value={name}
                    />
                    <TextareaField
                        className="TaxonomyFieldDescriptionInput"
                        disabled={disabled}
                        id={key}
                        onChange={this.OnTaxonomyDescription}
                        placeholder="Description..."
                        value={description}
                    />
                    <div className="TaxonomyFieldTerms">
                        {TermReorderName ? (
                            <div
                                className="TaxonomyFieldTermItemCursor"
                                ref={cursor => this.Cursor = cursor}
                                key={TermReorder}
                            >
                                {TermReorderName}
                            </div>
                        ) : ""}
                        {TermItems}
                        <div className="TaxonomyFieldTermInputContainer">
                            <div className="TaxonomyFieldTermInputWrapper">
                                <Wysiwyg
                                    className="TaxonomyFieldTermInput"
                                    disabled={disabled}
                                    id={key}
                                    minimal={2}
                                    onBlur={this.OnTermBlur}
                                    onChange={this.OnTermInput}
                                    placeholder="Add term..."
                                    ref={input => this.TermInputs[key] = input}
                                />
                                <IconButton
                                    feather="Plus"
                                    id={key}
                                    onClick={this.OnTermInputClick}
                                />
                            </div>
                            {suggestions && suggestions[0] === key ? this.renderSuggestions(...suggestions) : ""}
                        </div>
                    </div>
                </div>
                {taxonomyIndex === index + 1 ? <div className="TaxonomyFieldListItemInsert"></div> : ""}
            </div>
        );
    }
}

TaxonomyField.propTypes =
{
    className: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onChange: PropTypes.func,
    onEditTerm: PropTypes.func,
    onEditTerms: PropTypes.func,
    onRemoveTaxonomy: PropTypes.func,
    onTaxonomyDescription: PropTypes.func,
    onTaxonomyName: PropTypes.func,
    pool: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool]),
    value: PropTypes.object
};

TaxonomyField.defaultProps =
{
    className: "",
    disabled: false,
    error: false,
    id: "",
    label: "",
    onChange: () => {},
    onEditTerm: () => {},
    onEditTerms: () => {},
    onRemoveTaxonomy: () => {},
    onTaxonomyDescription: () => {},
    onTaxonomyName: () => {},
    onTaxonomyOrder: () => {},
    pool: false,
    value: {}
};

export default TaxonomyField;